import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { useI18n } from '@/lib/i18n';
import { ShoppingCart, Menu, X, User, LogOut, LayoutDashboard } from 'lucide-react';
import { useState } from 'react';

const LOGO_WHITE_URL = "https://customer-assets.emergentagent.com/job_rainforest-portal/artifacts/tketd0pp_AMAZONIA%20-%20Vertical%20Logo%20White%402x.png";

export default function Header() {
  const { user, isAdmin, isPro, logout } = useAuth();
  const { itemCount, setCartOpen } = useCart();
  const { t, lang, changeLang, FLAGS, LANG_NAMES } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdminRoute = location.pathname.startsWith('/admin');
  if (isAdminRoute) return null;

  const navLinks = [
    { to: '/', label: t('nav_home') },
    { to: '/catalog', label: t('nav_catalog') },
    { to: '/avantages-pro', label: t('nav_advantages') },
    { to: '/about', label: t('nav_about') },
    { to: '/contact', label: t('nav_contact') },
  ];

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <header data-testid="main-header" className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
            <div className="bg-[#111] rounded-sm px-4 py-2 flex items-center justify-center">
              <img src={LOGO_WHITE_URL} alt="Amazonia" className="h-12 w-auto" />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.to ? 'text-[#00A5FF] border-b-2 border-[#00A5FF]' : 'text-[#111] hover:text-[#00A5FF]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Language selector */}
            <div className="hidden sm:flex items-center border border-black/10 rounded-sm overflow-hidden" data-testid="lang-selector">
              {['fr', 'es', 'en'].map((l) => (
                <button
                  key={l}
                  onClick={() => changeLang(l)}
                  className={`px-2 py-1 text-xs font-semibold transition-colors duration-200 ${lang === l ? 'bg-[#00A5FF] text-white' : 'text-black/50 hover:bg-[#F8F9FA]'}`}
                >
                  {FLAGS[l]} {LANG_NAMES[l]}
                </button>
              ))}
            </div>

            {user ? (
              <>
                {(isPro || isAdmin) && (
                  <Link to="/dashboard" data-testid="dashboard-link" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#111] hover:text-[#00A5FF] transition-colors duration-200">
                    <LayoutDashboard size={16} /><span>{t('nav_pro_space')}</span>
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin" data-testid="admin-link" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#111] text-white rounded-sm hover:bg-[#333] transition-colors duration-200">Admin</Link>
                )}
                <button onClick={() => setCartOpen(true)} data-testid="cart-button" className="relative p-2 text-[#111] hover:text-[#00A5FF] transition-colors duration-200">
                  <ShoppingCart size={20} />
                  {itemCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#E8231B] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center">{itemCount}</span>}
                </button>
                <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-black/10">
                  <Link to="/account" className="flex items-center gap-1.5 text-sm text-[#111] hover:text-[#00A5FF] transition-colors duration-200">
                    <User size={16} /><span className="max-w-[120px] truncate">{user.company_name}</span>
                  </Link>
                  <button onClick={handleLogout} data-testid="logout-button" className="p-1.5 text-[#111]/60 hover:text-[#E8231B] transition-colors duration-200"><LogOut size={16} /></button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="login-link" className="px-4 py-2 text-sm font-medium text-[#111] hover:text-[#00A5FF] transition-colors duration-200">{t('nav_login')}</Link>
                <Link to="/register" data-testid="register-link" className="px-4 py-2 text-sm font-semibold bg-[#00A5FF] text-white rounded-sm hover:bg-[#0090e0] hover:-translate-y-0.5 transition-all duration-200">{t('nav_register')}</Link>
              </div>
            )}

            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 text-[#111]">
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-black/10 py-3">
            {/* Mobile language selector */}
            <div className="flex gap-1 px-4 mb-3">
              {['fr', 'es', 'en'].map((l) => (
                <button key={l} onClick={() => { changeLang(l); }} className={`px-3 py-1.5 text-xs font-semibold rounded-sm ${lang === l ? 'bg-[#00A5FF] text-white' : 'border border-black/10'}`}>
                  {FLAGS[l]} {LANG_NAMES[l]}
                </button>
              ))}
            </div>
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-medium text-[#111] hover:text-[#00A5FF] hover:bg-[#F8F9FA]">{link.label}</Link>
            ))}
            {user && (
              <>
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-medium text-[#111]">{t('nav_pro_space')}</Link>
                <Link to="/orders" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-medium text-[#111]">{t('nav_my_orders')}</Link>
                {isAdmin && <Link to="/admin" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-medium text-[#111]">Admin</Link>}
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block w-full text-left px-4 py-2 text-sm font-medium text-[#E8231B]">{t('nav_logout')}</button>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
